import { useMatch, useNavigate, useSearch } from '@tanstack/react-router';
import { useState } from 'react';
import { useGetProjectsProjectId } from '@/entities/project/api/generated/api';
import { Skeleton } from '@/shared/ui';
import type { AIContextMenu } from '@/widgets/right-side-menu/lib/types';
import { ProjectDetailAIChatHeader } from './header/ProjectDetailAIChatHeader';
import { MatchingTabContent } from './matching/MatchingTabContent';
import { ProjectDetailAIChatContents } from './ProjectDetailAIChatContents';
import { DEFAULT_PROJECT_DETAIL_TAB, type ProjectDetailTab } from './projectDetailTab';

type Props = {
  projectId: string;
  aiContextMenu: AIContextMenu;
  setAiContextMenu: (context: AIContextMenu) => void;
};

/**
 * 事業詳細・メモページ用のサイドパネル統合コンポーネント。
 *
 * - 1 段目のタブ（マッチング / チャット）で表示を切り替える
 * - タブ状態は URL search params `tab` で永続化する（未指定時は `chat` 相当）
 * - マッチングタブでは `MatchingTabContent` を、チャットタブでは既存の `ProjectDetailAIChatContents` を描画する
 */
export const ProjectDetailAIChatPanel = ({ projectId, aiContextMenu, setAiContextMenu }: Props) => {
  const navigate = useNavigate();
  const [showHistory, setShowHistory] = useState(false);

  // 現在ページが事業詳細・メモのどちらかを判定し、タブ切替先 navigate の to を決める
  const isOnMemoPage = useMatch({
    from: '/_protected/projects/$projectId/memo',
    shouldThrow: false,
  });

  // 詳細ページとメモページで search のキーが揃っているため、現在ページに応じて読む
  const detailSearch = useSearch({
    from: '/_protected/projects/$projectId/',
    shouldThrow: false,
  });
  const memoSearch = useSearch({
    from: '/_protected/projects/$projectId/memo',
    shouldThrow: false,
  });
  const tab: ProjectDetailTab =
    (isOnMemoPage ? memoSearch?.tab : detailSearch?.tab) ?? DEFAULT_PROJECT_DETAIL_TAB;

  const handleTabChange = (next: ProjectDetailTab) => {
    if (isOnMemoPage) {
      navigate({
        to: '/projects/$projectId/memo',
        params: { projectId },
        search: (prev) => ({ ...prev, tab: next }),
        replace: true,
      });
    } else {
      navigate({
        to: '/projects/$projectId',
        params: { projectId },
        search: (prev) => ({ ...prev, tab: next }),
        replace: true,
      });
    }
  };

  const threadId =
    aiContextMenu?.route === 'projectDetail' && aiContextMenu.mode === 'agent_chat'
      ? aiContextMenu.threadId
      : undefined;

  // マッチングタブには事業詳細データが必要（Accordion の reference_chats を参照する）
  // 事業詳細ページ本体と同じクエリキーのため TanStack Query のキャッシュで dedup される
  const { data: project, isLoading: isProjectLoading } = useGetProjectsProjectId(projectId, {
    query: { enabled: tab === 'matching' },
  });

  return (
    <>
      <ProjectDetailAIChatHeader
        projectId={projectId}
        showHistory={showHistory}
        setShowHistory={setShowHistory}
        setAiContextMenu={setAiContextMenu}
        threadId={threadId}
        tab={tab}
        onTabChange={handleTabChange}
      />
      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
        {tab === 'matching' ? (
          isProjectLoading || !project ? (
            <div className="space-y-4">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          ) : (
            <MatchingTabContent project={project} />
          )
        ) : (
          <ProjectDetailAIChatContents
            projectId={projectId}
            aiContextMenu={aiContextMenu}
            setAiContextMenu={setAiContextMenu}
            showHistory={showHistory}
            setShowHistory={setShowHistory}
          />
        )}
      </div>
    </>
  );
};
